import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Key, ArrowLeft, RefreshCw, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';

export default function ResetPassword() {
  const { currentColors } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setValidating(false);
      setLoading(false);
    }
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.post(`${API}/auth/validate-reset-token`, { token });
      if (response.data.valid) {
        setTokenValid(true);
        setEmail(response.data.email);
      }
    } catch (error) {
      setTokenValid(false);
    } finally {
      setValidating(false);
      setLoading(false);
    }
  };

  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('One lowercase letter');
    if (!/\d/.test(pwd)) errors.push('One number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) errors.push('One special character');
    return errors;
  };

  const passwordStrength = () => {
    if (!password) return { strength: 0, label: '', color: '' };
    const errors = validatePassword(password);
    const strength = 5 - errors.length;
    if (strength <= 1) return { strength: 20, label: 'Very Weak', color: '#ef4444' };
    if (strength === 2) return { strength: 40, label: 'Weak', color: '#f97316' };
    if (strength === 3) return { strength: 60, label: 'Fair', color: '#eab308' };
    if (strength === 4) return { strength: 80, label: 'Good', color: '#22c55e' };
    return { strength: 100, label: 'Strong', color: '#10b981' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    const validationErrors = validatePassword(password);
    if (validationErrors.length > 0) {
      setError(`Password requirements: ${validationErrors.join(', ')}`);
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      setTimeout(() => navigate('/'), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const strength = passwordStrength();

  if (validating) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center p-4">
        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: currentColors.primary }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center p-4">
      <div 
        className="w-full max-w-md rounded-2xl p-8 backdrop-blur-xl"
        style={{ 
          backgroundColor: `${currentColors.background}cc`,
          border: `1px solid ${currentColors.border}`
        }}
      >
        <Link 
          to="/"
          className="flex items-center gap-2 text-sm mb-6 hover:opacity-80"
          style={{ color: currentColors.textMuted }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>

        <div className="text-center mb-8">
          <div 
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${currentColors.primary}20` }}
          >
            <Key className="w-8 h-8" style={{ color: currentColors.primary }} />
          </div>
          <h1 className="text-2xl font-cinzel mb-2" style={{ color: currentColors.primary }}>
            Reset Password
          </h1>
        </div>

        {!token ? (
          <div className="text-center">
            <div className="p-4 rounded-xl bg-red-500/10 text-red-400 mb-4">
              <AlertCircle className="w-6 h-6 mx-auto mb-2" />
              <p>No reset token provided</p>
            </div>
            <Link to="/forgot-password" className="text-sm underline" style={{ color: currentColors.primary }}>
              Request a new reset link
            </Link>
          </div>
        ) : !tokenValid ? (
          <div className="text-center">
            <div className="p-4 rounded-xl bg-red-500/10 text-red-400 mb-4">
              <AlertCircle className="w-6 h-6 mx-auto mb-2" />
              <p>Invalid or expired reset link</p>
            </div>
            <Link to="/forgot-password" className="text-sm underline" style={{ color: currentColors.primary }}>
              Request a new reset link
            </Link>
          </div>
        ) : success ? (
          <div className="space-y-4">
            <div 
              className="p-4 rounded-xl flex items-start gap-3"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}
            >
              <Check className="w-5 h-5 text-green-500 mt-0.5" />
              <div className="text-sm">
                <p className="text-green-500 font-medium">Password Reset Successfully!</p>
                <p style={{ color: currentColors.textMuted }}>
                  Redirecting you to login...
                </p>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-center mb-4" style={{ color: currentColors.textMuted }}>
              Resetting password for: <strong>{email}</strong>
            </p>
            
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 text-red-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}
            
            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="cosmic-input w-full pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: currentColors.textMuted }}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Password strength indicator */}
              {password && (
                <div className="mt-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: currentColors.textMuted }}>Password strength:</span>
                    <span style={{ color: strength.color }}>{strength.label}</span>
                  </div>
                  <div 
                    className="h-1.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: `${currentColors.textMuted}20` }}
                  >
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ 
                        width: `${strength.strength}%`,
                        backgroundColor: strength.color
                      }}
                    />
                  </div>
                </div>
              )}
              
              {/* Requirements */}
              <div className="mt-2 text-xs space-y-1" style={{ color: currentColors.textMuted }}>
                <p className={password.length >= 8 ? 'text-green-500' : ''}>
                  {password.length >= 8 ? '✓' : '○'} At least 8 characters
                </p>
                <p className={/[A-Z]/.test(password) ? 'text-green-500' : ''}>
                  {/[A-Z]/.test(password) ? '✓' : '○'} One uppercase letter
                </p>
                <p className={/[a-z]/.test(password) ? 'text-green-500' : ''}>
                  {/[a-z]/.test(password) ? '✓' : '○'} One lowercase letter
                </p>
                <p className={/\d/.test(password) ? 'text-green-500' : ''}>
                  {/\d/.test(password) ? '✓' : '○'} One number
                </p>
                <p className={/[!@#$%^&*(),.?":{}|<>]/.test(password) ? 'text-green-500' : ''}>
                  {/[!@#$%^&*(),.?":{}|<>]/.test(password) ? '✓' : '○'} One special character
                </p>
              </div>
            </div>
            
            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="cosmic-input w-full"
                required
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
              )}
            </div>
            
            <button
              type="submit"
              disabled={loading || strength.strength < 100 || password !== confirmPassword}
              className="cosmic-btn w-full flex items-center justify-center gap-2"
            >
              {loading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Key className="w-5 h-5" />
                  Reset Password
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
